"use client";

import { useState } from "react";

import { Button, ButtonProps } from "@/components/ui";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { cx } from "@/lib/tailwind-utils";

interface Props {
  isOpen?: boolean;
  setIsOpen?: (val: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  subtitle: string;
  children?: React.ReactNode;
  customContent?: React.ReactNode;
  confirmText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: ButtonProps["variant"];
  /** When true, the dialog does not trap focus or lock scroll (allows interaction behind). */
  modal?: boolean;
}

const DeleteConfirmationModal: React.FC<Props> = ({
  isOpen: controlledIsOpen,
  setIsOpen: setControlledIsOpen,
  onConfirm,
  title,
  subtitle,
  children,
  customContent,
  confirmText = "Confirm",
  disabled = false,
  isLoading = false,
  variant = "destructive",
  modal,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = setControlledIsOpen ?? setInternalIsOpen;

  const handleConfirm = async () => {
    if (isLoading) return;
    await onConfirm();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} modal={modal}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="sm:max-w-lg"
        // Non-modal dialogs: prevent Radix from auto-dismissing on outside
        // pointer events.  The originating click (e.g. "Accept Selected") is
        // still propagating when the dialog first renders, so Radix's
        // DismissableLayer treats it as an outside click and immediately
        // fires onOpenChange(false).  Blocking these events lets the dialog
        // stay open; users dismiss via the Cancel / Confirm buttons instead.
        {...(modal === false && {
          onPointerDownOutside: (e: Event) => e.preventDefault(),
          onInteractOutside: (e: Event) => e.preventDefault(),
        })}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mt-1 text-body leading-6">
            {subtitle}
          </DialogDescription>
        </DialogHeader>

        {customContent && <DialogBody>{customContent}</DialogBody>}

        <DialogFooter className={cx(!customContent && "pt-0")}>
          <DialogClose asChild>
            <Button
              variant="secondary"
              className="w-fit"
              onClick={() => !isLoading && setIsOpen(false)}
              disabled={disabled || isLoading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            className="w-fit"
            onClick={handleConfirm}
            variant={variant}
            disabled={disabled || isLoading}
            isLoading={isLoading}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
